/**
 * Admin → "Sales Leads" tab.
 * Lists every prospect who spun up a live demo via the Landing page, with a
 * pipeline pill (new / contacted / qualified / demo_scheduled / won / lost)
 * and a one-click "Impersonate Demo PM" jump into their sandbox.
 */
import { useEffect, useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, Search, UserCog, RefreshCw, Trash2, ExternalLink, Mail, Phone, Building, MapPin, Clock, Key, Copy, Send, MessageSquare, HardHat } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Passkey formula (must match backend `compute_expected_passkey`
 * in /app/backend/routes/pm_tools.py):
 *   [first digit of address] + [company name, slugged] + [zip code]
 * where "slugged" = lowercase + strip every non-alphanumeric character.
 * Returns '—' when we don't have enough data (no address AND no zip AND
 * no company).
 */
const slugCompany = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const firstDigit = (text) => {
  const m = (text || '').match(/\d/);
  return m ? m[0] : '';
};
const computePasskey = (lead) => {
  const first = firstDigit(lead?.address);
  const slug = slugCompany(lead?.company);
  const zip = (lead?.zip_code || lead?.zip || '').toString().replace(/[^0-9a-zA-Z]/g, '');
  if (!first && !slug && !zip) return '';
  return `${first}${slug}${zip}`;
};

const STATUS_META = {
  new:            { label: 'New',            color: 'bg-blue-500 text-white' },
  contacted:      { label: 'Contacted',      color: 'bg-amber-500 text-black' },
  qualified:      { label: 'Qualified',      color: 'bg-purple-500 text-white' },
  demo_scheduled: { label: 'Demo scheduled', color: 'bg-indigo-500 text-white' },
  won:            { label: 'Won 🎉',         color: 'bg-emerald-500 text-white' },
  lost:           { label: 'Lost',           color: 'bg-zinc-500 text-white' },
  expired:        { label: 'Expired',        color: 'bg-zinc-700 text-zinc-300' },
};
const STATUS_ORDER = ['new', 'contacted', 'qualified', 'demo_scheduled', 'won', 'lost', 'expired'];

export default function SalesLeadsTab() {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (q) params.set('q', q);
      const res = await fetch(`${API}/api/admin/leads?${params.toString()}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to load leads');
        return;
      }
      setLeads(data.leads || []);
      setCounts(data.counts_by_status || {});
    } catch (err) {
      toast.error('Network error');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!q) return leads;
    const qq = q.toLowerCase();
    return leads.filter(l =>
      (l.name || '').toLowerCase().includes(qq) ||
      (l.company || '').toLowerCase().includes(qq) ||
      (l.work_email || '').toLowerCase().includes(qq));
  }, [leads, q]);

  const updateStatus = async (lead, newStatus) => {
    try {
      const res = await fetch(`${API}/api/admin/leads/${lead.lead_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Status updated to ${STATUS_META[newStatus]?.label || newStatus}`);
      load();
      if (selectedLead?.lead_id === lead.lead_id) setSelectedLead(data);
    } catch (err) { toast.error('Network error'); }
  };

  const saveNotes = async (lead, notes) => {
    try {
      const res = await fetch(`${API}/api/admin/leads/${lead.lead_id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success('Notes saved');
      setSelectedLead(data);
      load();
    } catch (err) { toast.error('Network error'); }
  };

  const impersonate = async (lead) => {
    if (!window.confirm(`Jump into ${lead.name}'s demo as the Property Manager?\n\nYou can return to your admin session via the gold banner at the top of the screen.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/leads/${lead.lead_id}/impersonate`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Now viewing as ${data.user?.name}`);
      window.location.href = '/property-manager-dashboard';
    } catch (err) { toast.error('Network error'); }
  };

  const joinAsOps = async (lead) => {
    if (!window.confirm(
      `Join ${lead.name}'s demo as an Operations Supervisor?\n\n`
      + `You'll drop into the field-agent experience — QR clock-ins, live GPS, `
      + `daily reports — using their demo sandbox. Great for walking a prospect `
      + `through the Staff app while their PM is in another tab.`
    )) return;
    try {
      const res = await fetch(`${API}/api/admin/leads/${lead.lead_id}/impersonate-as-ops`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Joined as ${data.user?.name} (${data.impersonation_kind})`);
      window.location.href = '/staff-dashboard';
    } catch (err) { toast.error('Network error'); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete ${lead.name} · ${lead.company} + wipe their demo org?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/leads/${lead.lead_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Removed lead + ${data.users || 0} demo users, ${data.properties || 0} properties`);
      if (selectedLead?.lead_id === lead.lead_id) setSelectedLead(null);
      load();
    } catch (err) { toast.error('Network error'); }
  };

  const sendPasskey = async (lead, channel) => {
    const label = channel === 'sms' ? 'SMS' : 'email';
    const target = channel === 'sms' ? lead.phone : lead.work_email;
    if (!target) {
      toast.error(`Lead has no ${channel === 'sms' ? 'phone number' : 'work email'} on file.`);
      return;
    }
    if (!window.confirm(`Send the demo passkey to ${target} via ${label}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/leads/${lead.lead_id}/send-passkey-${channel}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || `Failed to send ${label}`); return; }
      toast.success(`Passkey sent to ${target} via ${label}${data.passkey ? ` (${data.passkey})` : ''}`);
      load();
    } catch (err) { toast.error('Network error'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-sales-leads-tab">
      {/* Header + KPIs */}
      <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Users className="h-6 w-6 text-[#FFD60A]" />
              SALES LEADS — INBOUND DEMOS
            </h2>
            <p className="text-sm text-zinc-400 mt-1">
              Every prospect who spun up a live NECS demo from the landing page. Each demo lasts 7 days and lives in its own private sandbox.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS_ORDER.map((s) => (
              <div key={s} className="text-center bg-zinc-800 rounded-lg px-3 py-1.5">
                <div className={`inline-block h-2 w-2 rounded-full mr-1 align-middle ${STATUS_META[s]?.color || 'bg-zinc-500'}`} />
                <span className="text-xs text-zinc-400">{STATUS_META[s]?.label || s}:</span>
                <span className="ml-1 text-sm font-bold text-white">{counts[s] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, company, or email…"
              className="pl-9 bg-zinc-800 border-zinc-700 text-white"
              data-testid="leads-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px] bg-zinc-800 border-zinc-700 text-white" data-testid="leads-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={load} disabled={loading} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="leads-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Passkey formula banner — sales reps can read this off during
            demo calls and quote the exact key back to each prospect. */}
        <div
          className="mt-3 flex items-start gap-2 rounded-lg border border-[#C9A962]/40 bg-[#C9A962]/10 px-3 py-2 text-xs text-zinc-200"
          data-testid="leads-passkey-formula-banner"
        >
          <Key className="h-4 w-4 mt-0.5 text-[#C9A962] shrink-0" />
          <div className="leading-relaxed">
            <div className="font-bold text-[#C9A962] uppercase tracking-widest text-[10px]">
              Sales Demo Passkey Formula
            </div>
            <div>
              <code className="bg-black/50 px-1.5 py-0.5 rounded text-[#FFD60A]">[first digit of address]</code>
              <span className="mx-1 text-zinc-500">+</span>
              <code className="bg-black/50 px-1.5 py-0.5 rounded text-[#FFD60A]">[company name — lowercase, no spaces or punctuation]</code>
              <span className="mx-1 text-zinc-500">+</span>
              <code className="bg-black/50 px-1.5 py-0.5 rounded text-[#FFD60A]">[zip code]</code>
            </div>
            <div className="text-zinc-400 text-[11px]">
              Example: <span className="text-white">123 Main St</span> · <span className="text-white">ABC Realty, LLC</span> · <span className="text-white">76017</span>{' '}
              → <code className="text-emerald-300">1abcrealtyllc76017</code>
            </div>
          </div>
        </div>
      </Card>

      {/* Body — split: table + detail panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-zinc-800 text-zinc-400">
              <tr>
                <th className="px-3 py-2 text-left">Lead</th>
                <th className="px-3 py-2 text-left">Company</th>
                <th className="px-3 py-2 text-left">Passkey</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Signed up</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-3 py-10 text-center text-zinc-500">
                    {loading ? 'Loading…' : 'No leads match your filters. Share the landing page to start collecting demos.'}
                  </td>
                </tr>
              )}
              {filtered.map((lead) => {
                const st = STATUS_META[lead.status] || STATUS_META.new;
                const passkey = computePasskey(lead);
                return (
                  <tr
                    key={lead.lead_id}
                    className={`border-t border-zinc-800 hover:bg-zinc-800/60 cursor-pointer ${selectedLead?.lead_id === lead.lead_id ? 'bg-zinc-800' : ''}`}
                    onClick={() => setSelectedLead(lead)}
                    data-testid={`leads-row-${lead.lead_id}`}
                  >
                    <td className="px-3 py-2">
                      <div className="text-white font-semibold">{lead.name}</div>
                      <div className="text-xs text-zinc-400">{lead.work_email}</div>
                    </td>
                    <td className="px-3 py-2 text-zinc-200">
                      {lead.company}
                      <div className="text-xs text-zinc-500">{lead.property_count || '?'} properties · {lead.city || '—'}, {lead.state || '—'}</div>
                    </td>
                    <td className="px-3 py-2" data-testid={`leads-passkey-cell-${lead.lead_id}`}>
                      {passkey ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            try {
                              navigator.clipboard?.writeText(passkey);
                              toast.success(`Passkey copied: ${passkey}`);
                            } catch (_) { /* noop */ }
                          }}
                          className="inline-flex items-center gap-1 font-mono text-[11px] bg-black/40 hover:bg-black/60 text-emerald-300 border border-[#C9A962]/40 rounded px-2 py-1 max-w-[180px] truncate transition"
                          title={`Click to copy · Formula: ${firstDigit(lead.address) || '?'} + ${slugCompany(lead.company) || 'company'} + ${lead.zip_code || lead.zip || 'zip'}`}
                          data-testid={`leads-passkey-copy-${lead.lead_id}`}
                        >
                          <Key className="h-3 w-3 text-[#C9A962] shrink-0" />
                          <span className="truncate">{passkey}</span>
                        </button>
                      ) : (
                        <span className="text-[11px] text-zinc-500 italic" title="Address + zip missing — passkey will finalize once the lead completes onboarding.">
                          n/a
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={st.color}>{st.label}</Badge>
                      {lead.is_expired && lead.status !== 'expired' && (
                        <div className="text-[10px] text-red-400 mt-0.5">demo expired</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-zinc-400 text-xs">
                      {lead.created_at ? new Date(lead.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); sendPasskey(lead, 'sms'); }}
                          className="bg-blue-600 hover:bg-blue-500 text-white h-7 w-7 p-0 disabled:opacity-40"
                          data-testid={`leads-send-sms-${lead.lead_id}`}
                          disabled={!lead.phone || !passkey}
                          title={
                            !lead.phone
                              ? 'No phone on file'
                              : !passkey
                                ? 'Address + zip missing — passkey cannot be computed'
                                : `Send passkey via SMS to ${lead.phone}`
                          }
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); sendPasskey(lead, 'email'); }}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 w-7 p-0 disabled:opacity-40"
                          data-testid={`leads-send-email-${lead.lead_id}`}
                          disabled={!lead.work_email || !passkey}
                          title={
                            !lead.work_email
                              ? 'No work email on file'
                              : !passkey
                                ? 'Address + zip missing — passkey cannot be computed'
                                : `Send passkey via email to ${lead.work_email}`
                          }
                        >
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); impersonate(lead); }}
                          className="bg-[#C9A962] hover:bg-[#b89651] text-black h-7 px-2 text-xs"
                          data-testid={`leads-impersonate-${lead.lead_id}`}
                          disabled={lead.status === 'expired'}
                          title={lead.status === 'expired' ? 'Demo has been purged' : 'Enter as demo PM'}
                        >
                          <UserCog className="h-3 w-3 mr-1" />Enter
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); joinAsOps(lead); }}
                          className="bg-orange-600 hover:bg-orange-500 text-white h-7 px-2 text-xs"
                          data-testid={`leads-join-ops-${lead.lead_id}`}
                          disabled={lead.status === 'expired'}
                          title={
                            lead.status === 'expired'
                              ? 'Demo has been purged'
                              : 'Join demo as Operations Supervisor (Staff app)'
                          }
                        >
                          <HardHat className="h-3 w-3 mr-1" />Ops
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); remove(lead); }}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-7 px-2 text-xs"
                          data-testid={`leads-delete-${lead.lead_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <SalesLeadDetail
          lead={selectedLead}
          onStatusChange={(s) => updateStatus(selectedLead, s)}
          onNotesChange={(n) => saveNotes(selectedLead, n)}
        />
      </div>
    </div>
  );
}

function SalesLeadDetail({ lead, onStatusChange, onNotesChange }) {
  const [notesDraft, setNotesDraft] = useState('');
  useEffect(() => { setNotesDraft(lead?.notes || ''); }, [lead?.lead_id, lead?.notes]);

  if (!lead) {
    return (
      <Card className="bg-zinc-900 border border-zinc-800 p-6 text-center text-zinc-500" data-testid="leads-detail-empty">
        Select a lead to see contact info, demo credentials, and pipeline actions.
      </Card>
    );
  }

  const st = STATUS_META[lead.status] || STATUS_META.new;

  return (
    <Card className="bg-zinc-900 border-2 border-[#C9A962]/50 p-5 space-y-3" data-testid="leads-detail-panel">
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{lead.name}</h3>
          <Badge className={st.color}>{st.label}</Badge>
        </div>
        <div className="text-sm text-zinc-400">{lead.company}</div>
      </div>

      <div className="text-xs text-zinc-300 space-y-1">
        <div className="flex items-center gap-2"><Mail className="h-3 w-3 text-zinc-500" />{lead.work_email}</div>
        {lead.phone && <div className="flex items-center gap-2"><Phone className="h-3 w-3 text-zinc-500" />{lead.phone}</div>}
        <div className="flex items-center gap-2"><Building className="h-3 w-3 text-zinc-500" />{lead.property_count || '?'} properties</div>
        <div className="flex items-center gap-2"><MapPin className="h-3 w-3 text-zinc-500" />{lead.city || '—'}, {lead.state || '—'}</div>
        <div className="flex items-center gap-2">
          <Clock className="h-3 w-3 text-zinc-500" />
          Demo expires {lead.expires_at ? new Date(lead.expires_at).toLocaleString() : '—'}
        </div>
      </div>

      {/* Calculated passkey — the exact string sales reps quote to the
          prospect over the phone. Click to copy. */}
      {(() => {
        const pk = computePasskey(lead);
        if (!pk) return null;
        return (
          <div className="rounded-md border border-[#C9A962]/40 bg-black/40 p-2" data-testid="leads-detail-passkey">
            <div className="text-[10px] uppercase tracking-widest text-[#C9A962] flex items-center gap-1">
              <Key className="h-3 w-3" /> Calculated Passkey
            </div>
            <button
              type="button"
              onClick={() => {
                try { navigator.clipboard?.writeText(pk); toast.success('Passkey copied'); } catch (_) { /* noop */ }
              }}
              className="mt-1 w-full text-left font-mono text-sm text-emerald-300 flex items-center justify-between hover:text-emerald-200"
              data-testid="leads-detail-passkey-copy"
            >
              <span className="truncate">{pk}</span>
              <Copy className="h-3 w-3 text-zinc-400 shrink-0 ml-2" />
            </button>
            <div className="text-[10px] text-zinc-500 mt-1">
              {firstDigit(lead.address) || '?'} + {slugCompany(lead.company) || 'company'} + {lead.zip_code || lead.zip || 'zip'}
            </div>
          </div>
        );
      })()}

      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Move to stage</div>
        <div className="flex gap-1 flex-wrap">
          {STATUS_ORDER.filter(s => s !== 'expired').map(s => (
            <Button
              key={s}
              size="sm"
              variant={s === lead.status ? 'default' : 'outline'}
              onClick={() => onStatusChange(s)}
              className={`h-7 text-xs ${s === lead.status ? 'bg-[#C9A962] text-black hover:bg-[#b89651]' : 'border-zinc-700 text-zinc-300'}`}
              data-testid={`leads-stage-${s}`}
            >
              {STATUS_META[s]?.label || s}
            </Button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Notes</div>
        <Textarea
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          rows={4}
          placeholder="Follow-up log, contract terms, blockers…"
          className="bg-zinc-800 border-zinc-700 text-white text-xs"
          data-testid="leads-notes-input"
        />
        <Button
          size="sm"
          onClick={() => onNotesChange(notesDraft)}
          className="mt-2 bg-zinc-700 hover:bg-zinc-600 text-white h-7 text-xs"
          data-testid="leads-save-notes-btn"
        >
          Save notes
        </Button>
      </div>

      {Array.isArray(lead.demo_credentials) && lead.demo_credentials.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Provisioned demo accounts</div>
          <div className="space-y-1 text-[11px] font-mono max-h-36 overflow-y-auto">
            {lead.demo_credentials.map(c => (
              <div key={c.email} className="bg-zinc-800 rounded p-1.5">
                <span className="text-zinc-400">{c.role}:</span> {c.email} / <span className="text-emerald-300">{c.password}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
